import { drawing } from '@progress/kendo-drawing';
import { eventMap } from './event-map';
import { Observable } from '../Observable';
import { CHANGE } from './../../constants';
import { extend } from './utils';
const elementOffset = drawing.util.elementOffset;
const translate = function (x, y, scale) {
    return 'translate3d(' + x + 'px,' + y + 'px,0) scale(' + scale + ')';
};
export class TapCapture extends Observable {
    constructor(element, options) {
        super();
        const domElement = element[0] || element;
        this.element = domElement;
        this.capture = false;
        this._pressHandler = this._press.bind(this);
        this._releaseHandler = this._release.bind(this);
        eventMap.down.split(' ').forEach((event) => {
            domElement.addEventListener(event, this._pressHandler, true);
        });
        eventMap.up.split(' ').forEach((event) => {
            domElement.addEventListener(event, this._releaseHandler, true);
        });
        this.bind([
            'press',
            'release'
        ], options || {});
    }
    captureNext() {
        this.capture = true;
    }
    cancelCapture() {
        this.capture = false;
    }
    _press(e) {
        this.trigger('press');
        if (this.capture) {
            e.preventDefault();
        }
    }
    _release(e) {
        this.trigger('release');
        if (this.capture) {
            e.preventDefault();
            this.cancelCapture();
        }
    }
    destroy() {
        const domElement = this.element;
        eventMap.down.split(' ').forEach((event) => {
            domElement.removeEventListener(event, this._pressHandler, true);
        });
        eventMap.up.split(' ').forEach((event) => {
            domElement.removeEventListener(event, this._releaseHandler, true);
        });
    }
}
export class PaneDimension extends Observable {
    constructor(options) {
        super();
        this.forcedEnabled = false;
        extend(this, options);
        this.scale = 1;
        if (this.horizontal) {
            this.measure = 'offsetWidth';
            this.scrollSize = 'scrollWidth';
            this.axis = 'x';
        }
        else {
            this.measure = 'offsetHeight';
            this.scrollSize = 'scrollHeight';
            this.axis = 'y';
        }
    }
    makeVirtual() {
        extend(this, {
            virtual: true,
            forcedEnabled: true,
            _virtualMin: 0,
            _virtualMax: 0
        });
    }
    virtualSize(min, max) {
        if (this._virtualMin !== min || this._virtualMax !== max) {
            this._virtualMin = min;
            this._virtualMax = max;
            this.update();
        }
    }
    outOfBounds(offset) {
        return offset > this.max || offset < this.min;
    }
    forceEnabled() {
        this.forcedEnabled = true;
    }
    getSize() {
        return this.container[this.measure];
    }
    getTotal() {
        return this.element[this.scrollSize];
    }
    rescale(scale) {
        this.scale = scale;
    }
    update(silent) {
        const total = this.virtual ? this._virtualMax : this.getTotal(), scaledTotal = total * this.scale, size = this.getSize();
        if (total === 0 && !this.forcedEnabled) {
            return;
        }
        this.max = this.virtual ? -this._virtualMin : 0;
        this.size = size;
        this.total = scaledTotal;
        this.min = Math.min(this.max, size - scaledTotal);
        this.minScale = size / total;
        this.centerOffset = (scaledTotal - size) / 2;
        this.enabled = this.forcedEnabled || scaledTotal > size;
        if (!silent) {
            this.trigger(CHANGE, this);
        }
    }
}
export class PaneDimensions extends Observable {
    constructor(options) {
        super();
        this.x = new PaneDimension(extend({
            horizontal: true
        }, options));
        this.y = new PaneDimension(extend({
            horizontal: false
        }, options));
        this.container = options.container;
        this.forcedMinScale = options.minScale;
        this.maxScale = options.maxScale || 100;
        this.bind(CHANGE, options);
    }
    rescale(newScale) {
        this.x.rescale(newScale);
        this.y.rescale(newScale);
        this.refresh();
    }
    centerCoordinates() {
        return {
            x: Math.min(0, -this.x.centerOffset),
            y: Math.min(0, -this.y.centerOffset)
        };
    }
    refresh() {
        this.x.update();
        this.y.update();
        this.enabled = this.x.enabled || this.y.enabled;
        this.minScale = this.forcedMinScale || Math.min(this.x.minScale, this.y.minScale);
        this.fitScale = Math.max(this.x.minScale, this.y.minScale);
        this.trigger(CHANGE);
    }
}
export class PaneAxis extends Observable {
    constructor(options) {
        super();
        extend(this, options);
    }
    outOfBounds() {
        return this.dimension.outOfBounds(this.movable[this.axis]);
    }
    dragMove(delta) {
        const dimension = this.dimension, axis = this.axis, movable = this.movable, position = movable[axis] + delta;
        if (!dimension.enabled) {
            return;
        }
        let dragDelta = delta;
        if (position < dimension.min && delta < 0 || position > dimension.max && delta > 0) {
            dragDelta *= this.resistance;
        }
        movable.translateAxis(axis, dragDelta);
        this.trigger(CHANGE, this);
    }
}
export class Pane {
    constructor(options) {
        let x, y;
        extend(this, {
            elastic: true
        }, options);
        const resistance = this.elastic ? 0.5 : 0;
        const movable = this.movable;
        this.x = x = new PaneAxis({
            axis: 'x',
            dimension: this.dimensions.x,
            resistance: resistance,
            movable: movable
        });
        this.y = y = new PaneAxis({
            axis: 'y',
            dimension: this.dimensions.y,
            resistance: resistance,
            movable: movable
        });
        this.userEvents.bind([
            'press',
            'move',
            'end',
            'gesturestart',
            'gesturechange'
        ], {
            gesturestart(e) {
                this.gesture = e;
                this.offset = elementOffset(this.dimensions.container);
            },
            press(e) {
                const closestAnchor = e.event.target.closest('a');
                if (closestAnchor && closestAnchor.matches('[data-navigate-on-press=true]')) {
                    e.sender.cancel();
                }
            },
            gesturechange(e) {
                const previousGesture = this.gesture, previousCenter = previousGesture.center, center = e.center, minScale = this.dimensions.minScale, maxScale = this.dimensions.maxScale;
                let scaleDelta = e.distance / previousGesture.distance;
                if (movable.scale <= minScale && scaleDelta < 1) {
                    scaleDelta += (1 - scaleDelta) * 0.8;
                }
                if (movable.scale * scaleDelta >= maxScale) {
                    scaleDelta = maxScale / movable.scale;
                }
                const offsetX = movable.x + this.offset.left, offsetY = movable.y + this.offset.top;
                const coordinates = {
                    x: (offsetX - previousCenter.x) * scaleDelta + center.x - offsetX,
                    y: (offsetY - previousCenter.y) * scaleDelta + center.y - offsetY
                };
                movable.scaleWith(scaleDelta);
                x.dragMove(coordinates.x);
                y.dragMove(coordinates.y);
                this.dimensions.rescale(movable.scale);
                this.gesture = e;
                e.preventDefault();
            },
            move(e) {
                if (e.event.target.tagName.match(/textarea|input/i)) {
                    return;
                }
                if (x.dimension.enabled || y.dimension.enabled) {
                    x.dragMove(e.x.delta);
                    y.dragMove(e.y.delta);
                    e.preventDefault();
                }
                else {
                    e.touch.skip();
                }
            },
            end(e) {
                e.preventDefault();
            }
        });
    }
}
export class Movable extends Observable {
    constructor(element) {
        super();
        this.element = element;
        this.element.style.transformOrigin = 'left top';
        this.x = 0;
        this.y = 0;
        this.scale = 1;
        const coordinates = translate(this.x, this.y, this.scale);
        this.element.style.transform = coordinates;
        this._saveCoordinates(coordinates);
    }
    translateAxis(axis, by) {
        this[axis] += by;
        this.refresh();
    }
    scaleTo(scale) {
        this.scale = scale;
        this.refresh();
    }
    scaleWith(scaleDelta) {
        this.scale *= scaleDelta;
        this.refresh();
    }
    translate(coordinates) {
        this.x += coordinates.x;
        this.y += coordinates.y;
        this.refresh();
    }
    moveAxis(axis, value) {
        this[axis] = value;
        this.refresh();
    }
    moveTo(coordinates) {
        extend(this, coordinates);
        this.refresh();
    }
    refresh() {
        let x = this.x, y = this.y;
        if (this.round) {
            x = Math.round(x);
            y = Math.round(y);
        }
        const newCoordinates = translate(x, y, this.scale);
        if (newCoordinates !== this.coordinates) {
            this.element.style.transform = newCoordinates;
            this._saveCoordinates(newCoordinates);
            this.trigger(CHANGE);
        }
    }
    _saveCoordinates(coordinates) {
        this.coordinates = coordinates;
    }
}
